import React, { memo } from "react";
import "./calendario.css";
import type { CalendarCellData, CellThirdKey } from "./useCalendarioLogic";

type Props = {
  cell: CalendarCellData;
  isToday?: boolean;

  // Highlight por tercio: 1=morning, 2=mid, 4=evening (0..7)
  selectedMask?: number;

  // Para caps "pill" según día inicio/fin del rango
  isStartDay?: boolean;
  isEndDay?: boolean;

  // Compatibilidad (si el Grid aún los pasa)
  startThird?: CellThirdKey;
  endThird?: CellThirdKey;

  onClick?: (cell: CalendarCellData, third: CellThirdKey, e: React.MouseEvent) => void;
  onDoubleClick?: (cell: CalendarCellData, third: CellThirdKey, e: React.MouseEvent) => void;
  onContextMenu?: () => void;
};

const thirdToMask = (t: CellThirdKey) =>
  t === "morning" ? 1 : t === "mid" ? 2 : 4;
const thirdIndex = (t: CellThirdKey) =>
  t === "morning" ? 0 : t === "mid" ? 1 : 2;

function CalendarioCell({
  cell,
  isToday,
  selectedMask = 0,
  isStartDay,
  isEndDay,
  onClick,
  onDoubleClick,
  onContextMenu,
}: Props) {
  const cls = [
    "cal-cell-hit",
    "has-thirds",
    selectedMask ? "has-selection" : "",
    isToday ? "is-today" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const isSelected = (t: CellThirdKey) => (selectedMask & thirdToMask(t)) !== 0;

  const firstSelectedIdx = (() => {
    if (selectedMask & 1) return 0;
    if (selectedMask & 2) return 1;
    if (selectedMask & 4) return 2;
    return -1;
  })();

  const lastSelectedIdx = (() => {
    if (selectedMask & 4) return 2;
    if (selectedMask & 2) return 1;
    if (selectedMask & 1) return 0;
    return -1;
  })();

  const isSameDayRange = !!isStartDay && !!isEndDay;

  const thirdClass = (t: CellThirdKey) => {
    const sel = isSelected(t);
    const idx = thirdIndex(t);

    const isFirstSel = sel && idx == firstSelectedIdx;
    const isLastSel = sel && idx == lastSelectedIdx;

    const capLeft = isFirstSel && (isStartDay || isSameDayRange);
    const capRight = isLastSel && (isEndDay || isSameDayRange);

    return [
      "cal-third",
      t === "morning"
        ? "third-morning"
        : t === "mid"
          ? "third-mid"
          : "third-evening",
      sel ? "is-selected" : "",
      capLeft ? "cap-left" : "",
      capRight ? "cap-right" : "",
    ]
      .filter(Boolean)
      .join(" ");
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onContextMenu?.();
  };

  return (
    <div className={cls} onContextMenu={handleContextMenu}>
      <div
        className={thirdClass("morning")}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(cell, "morning", e);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onDoubleClick?.(cell, "morning", e);
        }}
      />
      <div
        className={thirdClass("mid")}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(cell, "mid", e);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onDoubleClick?.(cell, "mid", e);
        }}
      />
      <div
        className={thirdClass("evening")}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(cell, "evening", e);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onDoubleClick?.(cell, "evening", e);
        }}
      />
    </div>
  );
}

// Memoizada (round 2026-09-11): la grilla admin dibuja una celda por día y por
// track —hasta ~427 días por fila—. CalendarioGrid le entrega props estables
// (el objeto celda calculado una vez por fila y handlers con identidad fija),
// así que una celda cuyo día no cambió no se vuelve a renderizar cuando se
// mueve la selección o se re-renderiza calendario.tsx.
export default memo(CalendarioCell);
